/**
 * Component Configuration for Webflow Component Rendering System
 *
 * Defines the complete component configuration including scenario mappings,
 * component definitions, and environment-specific settings.
 *
 * Primary Strategy: Show/hide pre-placed Webflow components using CSS display properties
 */

import type { ComponentSystemConfig } from '../types';

/**
 * Main component configuration object
 *
 * This configuration maps scenario codes to specific component visibility patterns:
 * - Scenario 1: AccsOutcomeLoanOnly
 * - Scenario 2: AccsOutcomeDsLoan
 * - Scenario 3: AccsOutcomeFallback
 * - Scenario 4: AccsOutcomeDsOnly
 * - Scenario 5: AccsOutcomeUnqualified
 * - Scenario 6: AccsOutcomeFallback
 * - Scenario 7: AccsOutcomeUnqualified
 * - Scenario 8: AccsOutcomeDsLoan
 * - Scenario 9: AccsOutcomeFallback
 * - Scenario 10: AccsOutcomeDsOnly
 * - Fallback (errors): AccsOutcomeFallback (best UX)
 */
export const COMPONENT_CONFIG: ComponentSystemConfig = {
  // Component Definitions
  components: {
    'AccsFormProcessing': {
      id: 'AccsFormProcessing',
      selector: '[data-component="accs-form-processing"]',
      displayName: 'ACCS Form Processing',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'form-processing'],
      hideClasses: ['component-hidden'],
    },

    'AccsOutcomeLoanOnly': {
      id: 'AccsOutcomeLoanOnly',
      selector: '[data-component="accs-outcome-loan-only"]',
      displayName: 'ACCS Outcome - Loan Only',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-loan-only'],
      hideClasses: ['component-hidden'],
    },

    'AccsOutcomeDsLoan': {
      id: 'AccsOutcomeDsLoan',
      selector: '[data-component="accs-outcome-ds-loan"]',
      displayName: 'ACCS Outcome - DS + Loan',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-ds-loan'],
      hideClasses: ['component-hidden'],
    },

    'AccsOutcomeFallback': {
      id: 'AccsOutcomeFallback',
      selector: '[data-component="accs-outcome-fallback"]',
      displayName: 'ACCS Outcome - Fallback',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-fallback'],
      hideClasses: ['component-hidden'],
    },

    'AccsOutcomeDsOnly': {
      id: 'AccsOutcomeDsOnly',
      selector: '[data-component="accs-outcome-ds-only"]',
      displayName: 'ACCS Outcome - DS Only',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-ds-only'],
      hideClasses: ['component-hidden'],
    },

    'AccsOutcomeUnqualified': {
      id: 'AccsOutcomeUnqualified',
      selector: '[data-component="accs-outcome-unqualified"]',
      displayName: 'ACCS Outcome - Unqualified',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-unqualified'],
      hideClasses: ['component-hidden'],
    },

    // CreditDirect Components
    'CdFormProcessing': {
      id: 'CdFormProcessing',
      selector: '[data-component="cd-form-processing"]',
      displayName: 'CreditDirect Form Processing',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'form-processing'],
      hideClasses: ['component-hidden'],
    },

    'CdOutcomePartnerOnly': {
      id: 'CdOutcomePartnerOnly',
      selector: '[data-component="cd-outcome-partner-only"]',
      displayName: 'CreditDirect Outcome - Partner Only',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-partner-only'],
      hideClasses: ['component-hidden'],
    },

    'CdOutcomeDsLoan': {
      id: 'CdOutcomeDsLoan',
      selector: '[data-component="cd-outcome-ds-loan"]',
      displayName: 'CreditDirect Outcome - DS + Loan',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-ds-loan'],
      hideClasses: ['component-hidden'],
    },

    'CdOutcomeAppProcessError': {
      id: 'CdOutcomeAppProcessError',
      selector: '[data-component="cd-outcome-app-process-error"]',
      displayName: 'CreditDirect Outcome - Application Process Error',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-app-process-error'],
      hideClasses: ['component-hidden'],
    },

    'CdOutcomeDsOnly': {
      id: 'CdOutcomeDsOnly',
      selector: '[data-component="cd-outcome-ds-only"]',
      displayName: 'CreditDirect Outcome - DS Only',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-ds-only'],
      hideClasses: ['component-hidden'],
    },

    'CdOutcomeUnqualified': {
      id: 'CdOutcomeUnqualified',
      selector: '[data-component="cd-outcome-unqualified"]',
      displayName: 'CreditDirect Outcome - Unqualified',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-unqualified'],
      hideClasses: ['component-hidden'],
    },

    'CdOutcomeLoanOnly': {
      id: 'CdOutcomeLoanOnly',
      selector: '[data-component="cd-outcome-loan-only"]',
      displayName: 'CreditDirect Outcome - Loan Only',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-loan-only'],
      hideClasses: ['component-hidden'],
    },

    'CdOutcomeStopGoAdditionalInformation': {
      id: 'CdOutcomeStopGoAdditionalInformation',
      selector: '[data-component="cd-outcome-additional-information"]',
      displayName: 'CreditDirect Outcome - Additional Information',
      defaultVisible: false,
      visibleDisplay: 'block',
      hiddenDisplay: 'none',
      showClasses: ['component-visible', 'outcome-additional-information'],
      hideClasses: ['component-hidden'],
    },
  },

  // Scenario Mappings
  scenarioMappings: [
    // Scenario 1: Loan Only
    {
      scenarioCodes: [1],
      componentsToShow: ['AccsOutcomeLoanOnly'],
      componentsToHide: ['AccsFormProcessing', 'AccsOutcomeDsLoan', 'AccsOutcomeFallback', 'AccsOutcomeDsOnly', 'AccsOutcomeUnqualified'],
      description: 'Scenario 1 - Loan Only',
    },

    // Scenario 2: DS + Loan
    {
      scenarioCodes: [2],
      componentsToShow: ['AccsOutcomeDsLoan'],
      componentsToHide: ['AccsFormProcessing', 'AccsOutcomeLoanOnly', 'AccsOutcomeFallback', 'AccsOutcomeDsOnly', 'AccsOutcomeUnqualified'],
      description: 'Scenario 2 - DS + Loan',
    },

    // Scenario 3: Fallback
    {
      scenarioCodes: [3],
      componentsToShow: ['AccsOutcomeFallback'],
      componentsToHide: ['AccsFormProcessing', 'AccsOutcomeLoanOnly', 'AccsOutcomeDsLoan', 'AccsOutcomeDsOnly', 'AccsOutcomeUnqualified'],
      description: 'Scenario 3 - Fallback',
    },

    // Scenario 4: DS Only
    {
      scenarioCodes: [4],
      componentsToShow: ['AccsOutcomeDsOnly'],
      componentsToHide: ['AccsFormProcessing', 'AccsOutcomeLoanOnly', 'AccsOutcomeDsLoan', 'AccsOutcomeFallback', 'AccsOutcomeUnqualified'],
      description: 'Scenario 4 - DS Only',
    },

    // Scenario 5: Unqualified
    {
      scenarioCodes: [5],
      componentsToShow: ['AccsOutcomeUnqualified'],
      componentsToHide: ['AccsFormProcessing', 'AccsOutcomeLoanOnly', 'AccsOutcomeDsLoan', 'AccsOutcomeFallback', 'AccsOutcomeDsOnly'],
      description: 'Scenario 5 - Unqualified',
    },

    // Scenario 6: Fallback
    {
      scenarioCodes: [6],
      componentsToShow: ['AccsOutcomeFallback'],
      componentsToHide: ['AccsFormProcessing', 'AccsOutcomeLoanOnly', 'AccsOutcomeDsLoan', 'AccsOutcomeDsOnly', 'AccsOutcomeUnqualified'],
      description: 'Scenario 6 - Fallback',
    },

    // Scenario 7: Unqualified
    {
      scenarioCodes: [7],
      componentsToShow: ['AccsOutcomeUnqualified'],
      componentsToHide: ['AccsFormProcessing', 'AccsOutcomeLoanOnly', 'AccsOutcomeDsLoan', 'AccsOutcomeFallback', 'AccsOutcomeDsOnly'],
      description: 'Scenario 7 - Unqualified',
    },

    // Scenario 8: DS + Loan
    {
      scenarioCodes: [8],
      componentsToShow: ['AccsOutcomeDsLoan'],
      componentsToHide: ['AccsFormProcessing', 'AccsOutcomeLoanOnly', 'AccsOutcomeFallback', 'AccsOutcomeDsOnly', 'AccsOutcomeUnqualified'],
      description: 'Scenario 8 - DS + Loan',
    },

    // Scenario 9: Fallback
    {
      scenarioCodes: [9],
      componentsToShow: ['AccsOutcomeFallback'],
      componentsToHide: ['AccsFormProcessing', 'AccsOutcomeLoanOnly', 'AccsOutcomeDsLoan', 'AccsOutcomeDsOnly', 'AccsOutcomeUnqualified'],
      description: 'Scenario 9 - Fallback',
    },

    // Scenario 10: DS Only
    {
      scenarioCodes: [10],
      componentsToShow: ['AccsOutcomeDsOnly'],
      componentsToHide: ['AccsFormProcessing', 'AccsOutcomeLoanOnly', 'AccsOutcomeDsLoan', 'AccsOutcomeFallback', 'AccsOutcomeUnqualified'],
      description: 'Scenario 10 - DS Only',
    },
  ],

  // CreditDirect Scenario Mappings
  creditDirectScenarioMappings: [
    // Scenario 1: Partner Only
    {
      scenarioCodes: [1],
      componentsToShow: ['CdOutcomePartnerOnly'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomeDsLoan', 'CdOutcomeAppProcessError', 'CdOutcomeDsOnly', 'CdOutcomeUnqualified', 'CdOutcomeLoanOnly', 'CdOutcomeStopGoAdditionalInformation'],
      description: 'Scenario 1 - Partner Only',
    },

    // Scenario 2: DS + Loan
    {
      scenarioCodes: [2],
      componentsToShow: ['CdOutcomeDsLoan'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomePartnerOnly', 'CdOutcomeAppProcessError', 'CdOutcomeDsOnly', 'CdOutcomeUnqualified', 'CdOutcomeLoanOnly', 'CdOutcomeStopGoAdditionalInformation'],
      description: 'Scenario 2 - DS + Loan',
    },

    // Scenario 3: App Process Error
    {
      scenarioCodes: [3],
      componentsToShow: ['CdOutcomeAppProcessError'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomePartnerOnly', 'CdOutcomeDsLoan', 'CdOutcomeDsOnly', 'CdOutcomeUnqualified', 'CdOutcomeLoanOnly', 'CdOutcomeStopGoAdditionalInformation'],
      description: 'Scenario 3 - App Process Error',
    },

    // Scenario 4: DS Only
    {
      scenarioCodes: [4],
      componentsToShow: ['CdOutcomeDsOnly'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomePartnerOnly', 'CdOutcomeDsLoan', 'CdOutcomeAppProcessError', 'CdOutcomeUnqualified', 'CdOutcomeLoanOnly', 'CdOutcomeStopGoAdditionalInformation'],
      description: 'Scenario 4 - DS Only',
    },

    // Scenario 5: Additional Information capture (was Unqualified)
    {
      scenarioCodes: [5],
      componentsToShow: ['CdOutcomeStopGoAdditionalInformation'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomePartnerOnly', 'CdOutcomeDsLoan', 'CdOutcomeAppProcessError', 'CdOutcomeDsOnly', 'CdOutcomeUnqualified', 'CdOutcomeLoanOnly'],
      description: 'Scenario 5 - Additional Information',
    },

    // Scenario 6: Loan Only
    {
      scenarioCodes: [6],
      componentsToShow: ['CdOutcomeLoanOnly'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomePartnerOnly', 'CdOutcomeDsLoan', 'CdOutcomeAppProcessError', 'CdOutcomeDsOnly', 'CdOutcomeUnqualified', 'CdOutcomeStopGoAdditionalInformation'],
      description: 'Scenario 6 - Loan Only',
    },

    // Scenario 7: Additional Information capture (was Unqualified)
    {
      scenarioCodes: [7],
      componentsToShow: ['CdOutcomeStopGoAdditionalInformation'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomePartnerOnly', 'CdOutcomeDsLoan', 'CdOutcomeAppProcessError', 'CdOutcomeDsOnly', 'CdOutcomeUnqualified', 'CdOutcomeLoanOnly'],
      description: 'Scenario 7 - Additional Information',
    },

    // Scenario 8: DS + Loan
    {
      scenarioCodes: [8],
      componentsToShow: ['CdOutcomeDsLoan'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomePartnerOnly', 'CdOutcomeAppProcessError', 'CdOutcomeDsOnly', 'CdOutcomeUnqualified', 'CdOutcomeLoanOnly', 'CdOutcomeStopGoAdditionalInformation'],
      description: 'Scenario 8 - DS + Loan',
    },

    // Scenario 9: App Process Error
    {
      scenarioCodes: [9],
      componentsToShow: ['CdOutcomeAppProcessError'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomePartnerOnly', 'CdOutcomeDsLoan', 'CdOutcomeDsOnly', 'CdOutcomeUnqualified', 'CdOutcomeLoanOnly', 'CdOutcomeStopGoAdditionalInformation'],
      description: 'Scenario 9 - App Process Error',
    },

    // Scenario 10: DS Only
    {
      scenarioCodes: [10],
      componentsToShow: ['CdOutcomeDsOnly'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomePartnerOnly', 'CdOutcomeDsLoan', 'CdOutcomeAppProcessError', 'CdOutcomeUnqualified', 'CdOutcomeLoanOnly', 'CdOutcomeStopGoAdditionalInformation'],
      description: 'Scenario 10 - DS Only',
    },

    // Error scenarios (0, 97, 98): App Process Error
    // - Scenario 0: Technical/API error - shows retry button (default behavior)
    // - Scenario 97: Credit LOCKED (Experian) - 60s countdown + confirmation + 1 retry limit
    // - Scenario 98: Locked or no record (legacy) - same as 97 for now
    {
      scenarioCodes: [0, 97, 98],
      componentsToShow: ['CdOutcomeAppProcessError'],
      componentsToHide: ['CdFormProcessing', 'CdOutcomePartnerOnly', 'CdOutcomeDsLoan', 'CdOutcomeDsOnly', 'CdOutcomeUnqualified', 'CdOutcomeLoanOnly', 'CdOutcomeStopGoAdditionalInformation'],
      description: 'Error Scenarios - App Process Error',
    },

    // STUB: Scenario 95 - Credit FROZEN (federal legislation)
    // Future implementation: No retry button shown - user must contact credit bureau
    // Requires: New component variant or hide retry button via ComponentRenderer
    // {
    //   scenarioCodes: [95],
    //   componentsToShow: ['CdOutcomeAppProcessError'], // or dedicated frozen credit component
    //   componentsToHide: [...],
    //   description: 'Scenario 95 - Credit Frozen (Federal Legislation)',
    // },

    // STUB: Scenario 96 - No record found (data entry issue)
    // Future implementation: Immediate retry allowed, no countdown gate
    // User likely mistyped SSN/name - allow quick retry
    // {
    //   scenarioCodes: [96],
    //   componentsToShow: ['CdOutcomeAppProcessError'],
    //   componentsToHide: [...],
    //   description: 'Scenario 96 - No Record Found (Data Entry Issue)',
    // },
  ],

  // Environment Configurations
  environments: {
    development: {
      environment: 'development',
      apiBaseUrl: 'https://macenapi.maceinnovation.com/webhook/v1/credit-decision',
      additionalInfoApiUrl: 'https://dcapi.maceinnovation.com/webhook/v1/additional-information',
      debugMode: true,
      supabaseUrl: 'https://dwyvdtpzkkwnvowphvju.supabase.co',
      supabaseAnonKey: 'sb_publishable_t_xEURdLDU_4Tm-BiCTu7Q_xrG---4Z',
      polling: {
        intervalMs: 0, // Not used for direct API call
        timeoutMs: 120000, // 120 seconds timeout for API response (credit decisions can take time)
        maxAttempts: 1, // Single API call
      },
    },

    production: {
      environment: 'production',
      apiBaseUrl: 'https://macenapi.maceinnovation.com/webhook/v1/credit-decision',
      additionalInfoApiUrl: 'https://dcapi.maceinnovation.com/webhook/v1/additional-information',
      debugMode: false,
      supabaseUrl: 'https://dwyvdtpzkkwnvowphvju.supabase.co',
      supabaseAnonKey: 'sb_publishable_t_xEURdLDU_4Tm-BiCTu7Q_xrG---4Z',
      polling: {
        intervalMs: 0, // Not used for direct API call
        timeoutMs: 120000, // 120 seconds timeout for API response (credit decisions can take time)
        maxAttempts: 1, // Single API call
      },
      // Production-specific component overrides
      componentOverrides: [
        {
          id: 'AccsOutcomeLoanOnly',
          // Production might have different selectors or classes
          selector: '[data-component="accs-outcome-loan-only-prod"]',
        },
      ],
    },
  },

  // Fallback scenario - use fallback component for best UX on errors
  fallbackScenario: [3], // Maps to AccsOutcomeFallback
  creditDirectFallbackScenario: [3], // Maps to CdOutcomeAppProcessError

  // System Configuration
  system: {
    cssPrefix: 'prodebtco-component',
    enabled: true,
    gracefulFailure: true,
  },
};

/**
 * Helper function to get scenario mapping for a specific scenario code
 * @param scenarioCode - The scenario code to look up
 * @param brand - Optional brand identifier ('accs' or 'creditdirect'). If not provided, defaults to 'accs'
 */
export function getScenarioMapping(scenarioCode: number, brand?: 'accs' | 'creditdirect'): import('../types').ScenarioMapping | null {
  const mappings = brand === 'creditdirect'
    ? COMPONENT_CONFIG.creditDirectScenarioMappings
    : COMPONENT_CONFIG.scenarioMappings;

  return mappings.find(mapping =>
    mapping.scenarioCodes.includes(scenarioCode)
  ) || null;
}

/**
 * Helper function to get environment configuration
 */
export function getEnvironmentConfig(environment: string): import('../types').EnvironmentConfig | null {
  return COMPONENT_CONFIG.environments[environment] || null;
}

/**
 * Helper function to check if a scenario code is valid
 * @param scenarioCode - The scenario code to validate
 * @param brand - Optional brand identifier ('accs' or 'creditdirect'). If not provided, defaults to 'accs'
 */
export function isValidScenario(scenarioCode: number, brand?: 'accs' | 'creditdirect'): boolean {
  const mappings = brand === 'creditdirect'
    ? COMPONENT_CONFIG.creditDirectScenarioMappings
    : COMPONENT_CONFIG.scenarioMappings;

  return mappings.some(mapping =>
    mapping.scenarioCodes.includes(scenarioCode)
  );
}

/**
 * Helper function to get all component IDs
 */
export function getAllComponentIds(): string[] {
  return Object.keys(COMPONENT_CONFIG.components);
}

/**
 * Helper function to get fallback components (best UX on errors)
 * @param brand - Optional brand identifier ('accs' or 'creditdirect'). If not provided, defaults to 'accs'
 */
export function getFallbackComponents(brand?: 'accs' | 'creditdirect'): { show: string[]; hide: string[] } {
  const fallbackScenarioCode = brand === 'creditdirect'
    ? COMPONENT_CONFIG.creditDirectFallbackScenario[0]
    : COMPONENT_CONFIG.fallbackScenario[0];

  const fallbackMapping = getScenarioMapping(fallbackScenarioCode, brand);

  if (!fallbackMapping) {
    // Default fallback based on brand
    if (brand === 'creditdirect') {
      return {
        show: ['CdOutcomeAppProcessError'],
        hide: getAllComponentIds().filter(id => id !== 'CdOutcomeAppProcessError'),
      };
    }
    return {
      show: ['AccsOutcomeFallback'],
      hide: getAllComponentIds().filter(id => id !== 'AccsOutcomeFallback'),
    };
  }

  return {
    show: fallbackMapping.componentsToShow,
    hide: fallbackMapping.componentsToHide,
  };
}

/**
 * Get scenario-specific component actions
 * @param scenarioCode - The scenario code to get actions for
 * @param brand - Optional brand identifier ('accs' or 'creditdirect'). If not provided, defaults to 'accs'
 */
export function getComponentActions(scenarioCode: number, brand?: 'accs' | 'creditdirect'): {
  show: string[];
  hide: string[];
  isValid: boolean;
  useFallback: boolean;
} {
  const mapping = getScenarioMapping(scenarioCode, brand)

  if (!mapping) {
    // Use fallback scenario
    const fallback = getFallbackComponents(brand)
    return {
      show: fallback.show,
      hide: fallback.hide,
      isValid: false,
      useFallback: true,
    }
  }

  return {
    show: mapping.componentsToShow,
    hide: mapping.componentsToHide,
    isValid: true,
    useFallback: false,
  }
}

/**
 * Validation function to ensure configuration is valid
 */
export function validateConfiguration(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check that all components referenced in scenario mappings exist
  COMPONENT_CONFIG.scenarioMappings.forEach((mapping, index) => {
    [...mapping.componentsToShow, ...mapping.componentsToHide].forEach(componentId => {
      if (!COMPONENT_CONFIG.components[componentId]) {
        errors.push(`Scenario mapping ${index}: Component '${componentId}' not found in components definition`);
      }
    });
  });

  // Check that fallback scenario exists
  const fallbackMapping = getScenarioMapping(COMPONENT_CONFIG.fallbackScenario[0]);
  if (!fallbackMapping) {
    errors.push(`Fallback scenario '${COMPONENT_CONFIG.fallbackScenario[0]}' not found in scenario mappings`);
  }

  // Check environment configurations
  Object.entries(COMPONENT_CONFIG.environments).forEach(([envName, envConfig]) => {
    if (!envConfig.apiBaseUrl) {
      errors.push(`Environment '${envName}': Missing apiBaseUrl`);
    }
    if (!envConfig.polling || envConfig.polling.intervalMs <= 0) {
      errors.push(`Environment '${envName}': Invalid polling configuration`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Detect brand from current hostname
 * Returns 'creditdirect' if the hostname contains 'creditdirect'
 * Otherwise returns 'accs' as default
 */
export function detectBrand(): 'accs' | 'creditdirect' {
  if (typeof window === 'undefined') {
    return 'accs'; // Default for SSR
  }

  const hostname = window.location.hostname.toLowerCase();

  // Check for CreditDirect (with or without hyphen)
  if (hostname.includes('creditdirect') || hostname.includes('credit-direct')) {
    return 'creditdirect';
  }

  // Default to ACCS
  return 'accs';
}

/**
 * Get processing component ID for a brand
 */
export function getProcessingComponentId(brand?: 'accs' | 'creditdirect'): string {
  return brand === 'creditdirect' ? 'CdFormProcessing' : 'AccsFormProcessing';
}

/**
 * Get all outcome component IDs for a brand
 */
export function getOutcomeComponentIds(brand?: 'accs' | 'creditdirect'): string[] {
  if (brand === 'creditdirect') {
    return [
      'CdOutcomePartnerOnly',
      'CdOutcomeDsLoan',
      'CdOutcomeAppProcessError',
      'CdOutcomeDsOnly',
      'CdOutcomeUnqualified',
      'CdOutcomeLoanOnly',
      'CdOutcomeStopGoAdditionalInformation',
    ];
  }

  return [
    'AccsOutcomeLoanOnly',
    'AccsOutcomeDsLoan',
    'AccsOutcomeFallback',
    'AccsOutcomeDsOnly',
    'AccsOutcomeUnqualified',
  ];
}